/**
 * Booking confirmation emails.
 *
 * Two versions, per client feedback (Ariane, 2026-08-17, "new Booking Confirmations"):
 *
 *   - DIGITAL PRODUCTION (AI talents, location K.I.N.D.): remote work, no set, no call sheet.
 *     Project and Scope adjust to the booking's billing configuration (days / hours / KPI).
 *   - STANDARD PRODUCTION (on set, locations EB5 + MET): the on-site version, with call sheet,
 *     set/studio and the KPI rules.
 *
 * Both versions bill via the credit note procedure ("self-billing"), so they are only used when
 * the booking is queued for a credit note. Lano bookings keep the older template with the
 * "upload your invoice in Lano" instruction — see sendEmailNotification in ListViewConfirmed.js.
 */

/** Full postal addresses per location short code — the same data as the LEGENDE block. */
const LOCATION_ADDRESSES = {
  EB5: 'Essener Bogen 5, 22419 Hamburg',
  B28: 'Behringstrasse 28a, 22765 Hamburg',
  TR4: 'Tarpenring 4, 22419 Hamburg',
  OSB: 'Am Rosenkothen 2, 40880 Ratingen',
  MET: 'Holy-Allee 3, Metzingen',
  M: 'Holy-Allee 3, Metzingen',
};

/**
 * A digital production is a KIND order: it is booked with set = "digital Production"
 * (see CreateKindOrder.js / CalendarContext.js), and the talent works remotely.
 * The location short (K.I.N.D. / KIND) is accepted as a second signal for bookings whose
 * set field was never filled in.
 */
export function isDigitalProduction(booking) {
  if (!booking) return false;
  const set = String(pickLabel(booking.set) || '').trim().toLowerCase();
  if (set === 'digital production') return true;
  const loc = String(locationShort(booking) || '').trim().toUpperCase().replace(/\./g, '');
  return loc === 'KIND';
}

/**
 * What was booked, in the unit it is billed in: "9 hours" / "20 KPI" / "1 day".
 * Ariane: "the Scope: xx hours or xx KPI or 1 day", adjusting to the configuration.
 */
export function bookingScope(booking) {
  if (!booking) return '—';
  const type = String(booking.billing_type || 'daily').toLowerCase();

  if (type === 'hourly') {
    const hours = bookedHours(booking);
    return hours == null ? '—' : `${formatNumber(hours)} ${hours === 1 ? 'hour' : 'hours'}`;
  }
  if (type === 'kpi') {
    const kpi = numericKpi(booking);
    return kpi == null ? '—' : `${kpi} KPI`;
  }
  const days = bookedDays(booking);
  return `${days} ${days === 1 ? 'day' : 'days'}`;
}

/**
 * Subject + HTML body for the booking confirmation, picking the version from the booking.
 * @param {object} args
 * @param {object} args.booking - Booking record as returned by the API
 * @param {object} args.talent  - Talent record (name/Name, email/E_Mail, Tagessatz, Stundensatz)
 * @returns {{ subject: string, html: string, variant: 'digital'|'standard' }}
 */
export function buildBookingConfirmationEmail({ booking, talent }) {
  const digital = isDigitalProduction(booking);
  return {
    variant: digital ? 'digital' : 'standard',
    subject: 'Booking Confirmation - Set Cast',
    html: digital
      ? buildDigitalProductionEmailHtml({ booking, talent })
      : buildStandardProductionEmailHtml({ booking, talent }),
  };
}

/** DIGITAL PRODUCTION, AI-TALENTS, LOCATION: KIND */
export function buildDigitalProductionEmailHtml({ booking, talent }) {
  const scope = bookingScope(booking);
  return `
<p>Dear ${talentName(talent)},</p>

<p>We are pleased to confirm your booking with the following details:</p>

<p style="font-weight:bold; margin:0 0 8px 0;">Project Details</p>

<p style="margin:0 0 18px 0; line-height:1.7;">
  <strong>Project:</strong> ${projectLabel(booking, scope)}<br>
  <strong>Client:</strong> ${clientName(booking)}<br>
  <strong>Position:</strong> ${positionName(booking)}<br>
  <strong>Booking Date:</strong> ${bookingDateLabel(booking)}<br>
  <strong>Scope:</strong> ${scope}<br>
  <strong>Location:</strong> Remote / digital production<br>
  <strong>Rate:</strong> ${rateLabel(booking, talent)}<br>
  <strong>Additional Information:</strong> ${additionalInfo(booking)}
</p>

<p style="font-weight:bold; margin:0 0 8px 0;">Important Information</p>

<ul>
  <li>Please ensure your availability during the agreed working hours.</li>
  <li>In case of illness, emergency, or delay, please notify your <strong>Project Manager</strong>
      and <strong>krank@set-cast.com</strong> no later than <strong>08:00 AM (CET)</strong> on the
      day of the booking. When contacting us, please include the <strong>project title</strong> and
      <strong>client name</strong> in your email for faster processing.</li>
  <li>Any changes to availability should be communicated as early as possible.</li>
</ul>

${INVOICING_BLOCK}

<p style="font-weight:bold; margin:0 0 8px 0;">Fee &amp; Expenses</p>

<p>${FEE_PARAGRAPH}</p>

<p>Any additional expenses (e.g. credits, special tools, or other project-related costs) must be
agreed in advance with the respective project manager and will only be reimbursed if explicitly
approved prior to incurrence.</p>

${CLOSING_BLOCK}
`.trim();
}

/** STANDARD PRODUCTION, LOCATION: EB5, MET */
export function buildStandardProductionEmailHtml({ booking, talent }) {
  const setStudio = pickLabel(booking && booking.set);
  const info = additionalInfo(booking);
  return `
<p>Dear ${talentName(talent)},</p>

<p>We are pleased to confirm your booking with the following details:</p>

<ul>
  <li><strong>Title:</strong> ${bookingTitle(booking)}</li>
  <li><strong>Client:</strong> ${clientName(booking)}</li>
  <li><strong>Position:</strong> ${positionName(booking)}</li>
  <li><strong>Start Date:</strong> ${formatDate(startDateOf(booking))}</li>
  <li><strong>End Date:</strong> ${formatDate(endDateOf(booking))}</li>
  <li><strong>Time:</strong> ${timeRange(booking)} Please check the call sheet for your final call time</li>
  <li><strong>KPI:</strong> ${kpiLabel(booking)}</li>
  <li><strong>Rate:</strong> ${rateLabel(booking, talent)}</li>
  <li><strong>Location:</strong> ${locationAddress(booking)}</li>
  ${setStudio ? `<li><strong>Set / Studio:</strong> ${setStudio}</li>` : ''}
  ${info !== '—' ? `<li><strong>Additional Info:</strong> ${info}</li>` : ''}
</ul>

<p style="font-weight:bold; margin:0 0 8px 0;">Important Information</p>

<p>In case of illness or delay, please notify the distribution lists planning@orendtstudios.com
and krank@set-cast.com by 8:00 a.m. at the latest.</p>

<p>If you need to leave the set early, please inform Set Cast and the production team and check
with the producer whether the KPI has been reached.</p>

<p>For questions regarding KPIs, shooting schedules, call sheets, or briefings, please contact
planning@orendtstudios.com</p>

${INVOICING_BLOCK}

<p style="font-weight:bold; margin:0 0 8px 0;">Fee &amp; Expenses</p>

<p>${FEE_PARAGRAPH}</p>

${CLOSING_BLOCK}
`.trim();
}

// ─── Shared copy blocks ────────────────────────────────────────────────────────

/**
 * Self-billing via credit note. This replaces the old "please upload your invoice in Lano"
 * instruction: for these bookings SetCast raises the Gutschrift itself, so the talent must not
 * send an invoice — but the billing master data in TOBS has to be complete for it to work.
 */
const INVOICING_BLOCK = `
<p style="font-weight:bold; margin:0 0 8px 0;">Invoicing | Self-billing via credit note</p>

<p>Invoicing will be handled via the credit note procedure in TOBS.<br>
Please ensure that your <strong>bank and tax details, as well as your full address</strong>, are
complete and up to date in your TOBS profile under <strong>&ldquo;Billing Infos&rdquo;</strong>.
No separate invoice is required.</p>

<p>For questions regarding the status of your payment or accounting matters, please contact
<strong>support@set-cast.com</strong></p>
`.trim();

const FEE_PARAGRAPH =
  'The agreed daily rate covers all costs and incidental expenses associated with the provision ' +
  'of the contractually agreed services. These include, in particular, but are not limited to, ' +
  'travel expenses, accommodation costs, meal expenses, and all other expenses incurred in the ' +
  'course of providing the services. No additional costs will be incurred by the client.';

const CLOSING_BLOCK = `
<p>Thank you for your support. We look forward to working with you.</p>

<p>Best regards,</p>

<p><strong>Set Cast Team</strong></p>
`.trim();

// ─── Field helpers ─────────────────────────────────────────────────────────────

/** Booking fields arrive either as plain strings or as {label, value} select objects. */
function pickLabel(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'object') return value.label || value.name || value.short || '';
  return String(value);
}

function talentName(talent) {
  if (!talent) return 'Sir or Madam';
  return talent.name || talent.Name || 'Sir or Madam';
}

function bookingTitle(booking) {
  return orDash(pickLabel(booking && (booking.title || booking.boktitle)));
}

function clientName(booking) {
  return orDash(pickLabel(booking && booking.client));
}

function positionName(booking) {
  return orDash(pickLabel(booking && booking.position));
}

function additionalInfo(booking) {
  return orDash(pickLabel(booking && (booking.sonstige_info || booking.additional_info)));
}

/**
 * "KIND Order – 9 hours". The stored title always reads "KIND Order - N day(s)" (CreateKindOrder
 * writes it before the billing type is known), so for KIND orders the scope is re-derived —
 * Ariane: "Project: KIND Order – 1 Day or 20 KPI or 9 hours ... should adjust".
 */
function projectLabel(booking, scope) {
  const title = pickLabel(booking && (booking.title || booking.boktitle));
  if (/kind\s*order/i.test(title)) return `KIND Order – ${scope}`;
  return orDash(title);
}

function locationShort(booking) {
  const loc = booking && booking.location;
  if (loc == null || loc === '') return '';
  if (typeof loc === 'object') return loc.short || loc.name || '';
  return String(loc);
}

/**
 * Full postal address for the on-set version ("Essener Bogen 5, 22419 Hamburg"), which the
 * locations table cannot supply — it only stores name, short and city.
 */
function locationAddress(booking) {
  const loc = booking && booking.location;
  const short = String(locationShort(booking) || '').trim();
  const known = LOCATION_ADDRESSES[short.toUpperCase().replace(/\./g, '')];
  if (known) return known;
  if (loc && typeof loc === 'object') {
    const parts = [loc.name || loc.short, loc.city].filter(Boolean);
    if (parts.length) return parts.join(', ');
  }
  return orDash(short);
}

/** Rate as billed: "€43.75 per hour", "€25 per KPI", "€400 per day". */
function rateLabel(booking, talent) {
  const type = String((booking && booking.billing_type) || 'daily').toLowerCase();
  const custom = booking && (booking.custom_rate?.value ?? booking.customRate);
  const fallback = type === 'hourly'
    ? (talent && talent.Stundensatz)
    : (talent && talent.Tagessatz);
  const raw = custom != null && custom !== '' ? custom : fallback;
  const num = Number(raw);
  if (raw == null || raw === '' || isNaN(num)) return '—';
  const unit = type === 'hourly' ? ' per hour' : type === 'kpi' ? ' per KPI' : ' per day';
  return `€${formatNumber(num)}${unit}`;
}

function kpiLabel(booking) {
  const kpi = numericKpi(booking);
  return kpi == null ? '—' : String(kpi);
}

/** KPI is sometimes empty, sometimes a string, and has been seen holding a location object. */
function numericKpi(booking) {
  const value = booking && booking.kpi;
  if (value == null || value === '' || typeof value === 'object') return null;
  const num = parseFloat(value);
  return isNaN(num) ? null : Math.round(num);
}

function timeRange(booking) {
  const start = (booking && (booking.start_time || booking.startTime)) || 'N/A';
  const end = (booking && (booking.end_time || booking.endTime)) || 'N/A';
  return `${start} - ${end}`;
}

/**
 * Hours booked: the explicit booked_hours when set (KIND orders, fractions allowed), otherwise
 * derived from start/end time. Mirrors the Lano task calculation in ListViewConfirmed.js.
 */
function bookedHours(booking) {
  const explicit = Number(booking && booking.booked_hours);
  if (!isNaN(explicit) && explicit > 0) return explicit;
  const parse = (t) => {
    const m = String(t || '').trim().match(/^(\d{1,2}):(\d{2})/);
    return m ? Number(m[1]) + Number(m[2]) / 60 : null;
  };
  const s = parse(booking && (booking.start_time || booking.startTime));
  const e = parse(booking && (booking.end_time || booking.endTime));
  if (s == null || e == null) return null;
  let diff = e - s;
  if (diff < 0) diff += 24; // over midnight
  return Math.round(diff * 100) / 100 || null;
}

/** Days booked, inclusive of both ends; a single day when the dates are missing or equal. */
function bookedDays(booking) {
  const start = startDateOf(booking);
  const end = endDateOf(booking);
  if (!start || !end) return 1;
  const ms = end.getTime() - start.getTime();
  if (isNaN(ms) || ms < 0) return 1;
  return Math.floor(ms / 86400000) + 1;
}

function startDateOf(booking) {
  return toDate(booking && booking.start_date);
}

function endDateOf(booking) {
  return toDate(booking && booking.end_date) || startDateOf(booking);
}

/** "19.06.2026", or "19.06.2026 – 21.06.2026" when the booking spans several days. */
function bookingDateLabel(booking) {
  const start = formatDate(startDateOf(booking));
  const end = formatDate(endDateOf(booking));
  return start === end ? start : `${start} – ${end}`;
}

/** Accepts ISO strings, Date objects and Firestore {seconds} timestamps. */
function toDate(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === 'object' && value.seconds) return new Date(value.seconds * 1000);
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

function formatDate(date) {
  if (!date) return '—';
  const p = (n) => String(n).padStart(2, '0');
  return `${p(date.getDate())}.${p(date.getMonth() + 1)}.${date.getFullYear()}`;
}

/** 9 → "9", 43.75 → "43.75" (no trailing zeros on whole numbers). */
function formatNumber(n) {
  const num = Number(n);
  if (isNaN(num)) return String(n);
  return Number.isInteger(num) ? String(num) : String(Math.round(num * 100) / 100);
}

function orDash(value) {
  const s = String(value == null ? '' : value).trim();
  return s === '' ? '—' : s;
}
